/* eslint-disable react-refresh/only-export-components --
 * The registry is the router itself; splitting it to satisfy Fast Refresh
 * would just move the surface without adding value.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { LevelId, LevelNumber } from '@/state/types';
import { assertNever } from '@/utils/assert';

type LazyLevel = LazyExoticComponent<ComponentType<Record<string, never>>>;

function NotBuiltYetInner() {
  return (
    <div data-testid="level-not-built" role="status">
      This level hasn't been written yet. SoulCaptcha is sulking.
    </div>
  );
}

const NotBuiltYet: LazyLevel = lazy(() =>
  Promise.resolve({ default: NotBuiltYetInner })
);

const L01: LazyLevel = lazy(() => import('./L01_Liar'));
const L02: LazyLevel = lazy(() => import('./L02_NomenclatureNightmare'));
const L03: LazyLevel = lazy(() => import('./L03_Password'));
const L04: LazyLevel = lazy(() => import('./L04_Staring'));
const L05: LazyLevel = lazy(() => import('./L05_WakeUp'));
const L06: LazyLevel = lazy(() => import('./L06_Compliment'));
const L07: LazyLevel = lazy(() => import('./L07_HeavyButton'));
const L08: LazyLevel = lazy(() => import('./L08_Trolley'));
const L09: LazyLevel = lazy(() => import('./L09_Scream'));
const L10: LazyLevel = lazy(() => import('./L10_Checkpoint'));
const L11: LazyLevel = lazy(() => import('./L11_Dignity'));
const L12: LazyLevel = lazy(() => import('./L12_SillyClicks'));
const L13: LazyLevel = lazy(() => import('./L13_Cutaway'));
const L14: LazyLevel = lazy(() => import('./L14_ArtCritique'));
const L15: LazyLevel = lazy(() => import('./L15_StringDance'));
const L16: LazyLevel = lazy(() => import('./L16_Bureaucracy'));
const L17: LazyLevel = lazy(() => import('./L17_Biscuit'));
const _L18: LazyLevel = lazy(() => import('./L18_StressTest'));
const L19: LazyLevel = lazy(() => import('./L19_SoulCaptcha'));
const L20: LazyLevel = lazy(() => import('./L20_SecondCheckpoint'));
const L21: LazyLevel = lazy(() => import('./L21_Pebbles'));
const _L22: LazyLevel = lazy(() => import('./L22_PoliteCollision'));
const L23: LazyLevel = lazy(() => import('./L23_Tortoise'));
const L24: LazyLevel = lazy(() => import('./L24_TruthCaptcha'));
const L25: LazyLevel = lazy(() => import('./L25_Vending'));
const _L26: LazyLevel = lazy(() => import('./L26_SillyPaths'));
const _L27: LazyLevel = lazy(() => import('./L27_SelfEsteem'));
const L28: LazyLevel = lazy(() => import('./L28_MinistrySound'));
const L29: LazyLevel = lazy(() => import('./L29_AimCaptcha'));
const L30: LazyLevel = lazy(() => import('./L30_DesktopShortcut'));
const FalseEnding: LazyLevel = lazy(() => import('./FalseEnding'));
const Endgame: LazyLevel = lazy(() => import('./Endgame'));
const GameOver: LazyLevel = lazy(() => import('./GameOver'));

// Staging levels LA–LH: accessible via ?level=L33 through ?level=L40.
// Not in normal play progression — awaiting final sequence placement.
const LA: LazyLevel = lazy(() => import('./LA_AnyKey'));
const LB: LazyLevel = lazy(() => import('./LB_SocialSpace'));
const LC: LazyLevel = lazy(() => import('./LC_AutoCorrect'));
const LD: LazyLevel = lazy(() => import('./LD_TrainingVideo'));
const LE: LazyLevel = lazy(() => import('./LE_FeedbackSurvey'));
const LF: LazyLevel = lazy(() => import('./LF_EmpathyCalibration'));
const LG: LazyLevel = lazy(() => import('./LG_UnsubscribeLoop'));
const LH: LazyLevel = lazy(() => import('./LH_InverseCarousel'));
const LI: LazyLevel = lazy(() => import('./LI_HighGlossSqueak'));
const LK: LazyLevel = lazy(() => import('./LK_MeaningOfLife'));
const LEVEL_BY_NUMBER: Partial<Record<LevelNumber, LazyLevel>> = {
  0: LC,
  1: L01,
  2: L02,
  3: L03,
  4: L04,
  5: L05,
  6: L06,
  7: L07,
  8: L08,
  9: L09,
  10: L10,
  11: L11,
  12: L12,
  13: L13,
  14: L14,
  15: L15,
  16: L16,
  17: L17,
  18: LB,
  19: L19,
  20: L20,
  21: L21,
  22: LD,
  23: L23,
  24: L24,
  25: L25,
  26: LE,
  27: LG,
  28: L28,
  29: L29,
  30: L30,
  33: LA,
  34: LI,
  36: LK,
  38: LF,
  40: LH,
};

/**
 * Map a LevelId to its lazy-loaded component. Returns NotBuiltYet for any
 * level that hasn't been implemented — keeps the router total while the build
 * sequence is mid-way through levels.
 */
export function resolveLevel(id: LevelId): LazyLevel {
  switch (id.kind) {
    case 'LEVEL':
      return LEVEL_BY_NUMBER[id.n] ?? NotBuiltYet;
    case 'FALSE_ENDING':
      return FalseEnding;
    case 'ENDGAME':
      return Endgame;
    case 'GAME_OVER':
      return GameOver;
    default:
      return assertNever(id);
  }
}

export { NotBuiltYet };
